import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, ReactNode, useEffect, useContext, useCallback } from 'react';

import { ServiceCode } from '@/shared/types';

import { Loader } from '@/shared/ui';

import { captchaManager } from '@/middlewares/captcha/CaptchaManager';

import AuthContext from '@/contexts/Auth/AuthContext';

import api from '@/api';
import { queryKeys } from '@/api/queryKeys';
import { UserService } from '@/api/types';

import { die, on } from '../../eventBus';

interface ServicesContextValue {
  services: UserService[];
  isLoading: boolean;
  getServiceByCode: (code: ServiceCode) => UserService | null;
}

export const ServicesContext = createContext<ServicesContextValue>({
  services: [],
  isLoading: false,
  getServiceByCode: () => null,
});

export const ServicesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isInitRender, isLoggedIn } = useContext(AuthContext);
  const queryClient = useQueryClient();

  const { data: services = [], isLoading } = useQuery({
    queryKey: queryKeys.services.list(),
    queryFn: () => api.services.getAll().then((r) => r.data),
    enabled: !isInitRender,
  });

  const getServiceByCode = useCallback(
    (code: ServiceCode): UserService | null => {
      return services.find((service) => service.code === code) ?? null;
    },
    [services],
  );

  useEffect(() => {
    if (services.length > 0) {
      captchaManager.setServices(services);
    }
  }, [services]);

  useEffect(() => {
    const invalidate = (): void => {
      queryClient.invalidateQueries({ queryKey: queryKeys.services.list() });
    };

    on('update_services', invalidate);

    return (): void => {
      die('update_services');
    };
  }, [isInitRender, isLoggedIn, queryClient]);

  if (isLoading) {
    return <Loader centered />;
  }

  const contextValue: ServicesContextValue = {
    services,
    isLoading,
    getServiceByCode,
  };

  return <ServicesContext.Provider value={contextValue}>{children}</ServicesContext.Provider>;
};
